import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../context/I18nContext";
import { pathFor } from "../../lib/routes";

export default function Footer({ onOpenCookiePrefs }) {
  const { t, lang } = useI18n();
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#090D16] text-slate-300"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-300 font-bold"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Sb
              </span>
              <span
                className="text-white text-lg font-semibold"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {t("common.brand")}{" "}
                <span className="text-xs uppercase tracking-widest text-emerald-300">
                  {t("common.brandTag")}
                </span>
              </span>
            </div>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-4">
              {t("footer.sitemapTitle")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={pathFor("home", lang)}
                  data-testid="footer-home-link"
                  className="text-slate-400 hover:text-white no-underline"
                >
                  {t("nav.home")}
                </Link>
              </li>
              <li>
                <Link
                  to={pathFor("home", lang) + "#about"}
                  data-testid="footer-about-link"
                  className="text-slate-400 hover:text-white no-underline"
                >
                  {t("nav.about")}
                </Link>
              </li>
              <li>
                <Link
                  to={pathFor("home", lang) + "#how"}
                  data-testid="footer-how-link"
                  className="text-slate-400 hover:text-white no-underline"
                >
                  {t("nav.how")}
                </Link>
              </li>
              <li>
                <Link
                  to={pathFor("home", lang) + "#faq"}
                  data-testid="footer-faq-link"
                  className="text-slate-400 hover:text-white no-underline"
                >
                  {t("nav.faq")}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-4">
              {t("footer.legalTitle")}
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={pathFor("privacy", lang)}
                  data-testid="footer-privacy-link"
                  className="text-slate-400 hover:text-white no-underline"
                >
                  {t("nav.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={pathFor("cookies", lang)}
                  data-testid="footer-cookies-link"
                  className="text-slate-400 hover:text-white no-underline"
                >
                  {t("nav.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={pathFor("terms", lang)}
                  data-testid="footer-terms-link"
                  className="text-slate-400 hover:text-white no-underline"
                >
                  {t("nav.terms")}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={onOpenCookiePrefs}
                  data-testid="footer-cookie-prefs-button"
                  className="text-slate-400 hover:text-white text-sm bg-transparent p-0"
                >
                  {t("cookie.customize")}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-4">
              {t("footer.contactTitle")}
            </h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li>{t("footer.address")}</li>
              <li>
                <a
                  href={`mailto:${t("footer.email")}`}
                  data-testid="footer-email-link"
                  className="text-slate-300 hover:text-white no-underline"
                >
                  {t("footer.email")}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${t("footer.phone").replace(/\s+/g, "")}`}
                  data-testid="footer-phone-link"
                  className="text-slate-300 hover:text-white no-underline"
                >
                  {t("footer.phone")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-white/10 grid gap-3 md:grid-cols-2 text-xs text-slate-500 leading-relaxed">
          <div>
            <p className="text-slate-300 font-semibold">{t("footer.legalName")}</p>
            <p>{t("footer.ruc")}</p>
            <p>{t("footer.reg")}</p>
            <p>{t("footer.license")}</p>
            <p>{t("footer.jurisdiction")}</p>
            <p>{t("footer.year")}</p>
          </div>
          <div>
            <p>{t("footer.disclaimer")}</p>
            <p className="mt-3 text-slate-400">{t("footer.copyright")}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
